"use client";
import { ReactElement, ReactNode } from "react";
import ModalRoot, { type ModalAnimation } from "./ModalRoot/ModalRoot";
import ModalTrigger from "./ModalTrigger/ModalTrigger";
import ModalPortal from "./ModalPortal/ModalPortal";
import ModalOverlay from "./ModalOverlay/ModalOverlay";
import ModalContent, { ModalAccessibleName } from "./ModalContent/ModalContent";
import { ModalClose } from "./ModalClose/ModalClose";

type ModalProps = ModalAccessibleName & {
  children: ReactNode;
  ariaDescribedby?: string;
  /** Element that opens the modal on click (uncontrolled usage) */
  trigger?: ReactElement;
  /** Controlled usage, e.g. state from AppContext */
  open?: boolean;
  setOpen?: (open: boolean) => void;
  /** Overrides the default aria-label of the close button */
  closeLabel?: string;
  /** Render inside a positioned container instead of fullscreen (no scroll lock) */
  absolutePosition?: boolean;
  portalRef?: HTMLElement | null;
  contentClassName?: string;
  onMountedChange?: (mounted: boolean) => void;
  /** Staggered mode sequences ModalAnimatedContent before the overlay on exit. */
  animation?: ModalAnimation;
  /** Allows async content to wait until ready before its entrance animation. */
  contentReady?: boolean;
};

/**
 * The one modal for the whole template — use it for every modal / popup / lightbox,
 * so all of them share the same overlay, animation, close button, focus trap,
 * Escape handling and scroll lock. Never build a custom `role="dialog"` (ESLint errors).
 *
 * @example Uncontrolled
 * <Modal trigger={<Button>Open</Button>} ariaLabel="Image details">…</Modal>
 *
 * @example Controlled
 * <Modal open={isOpen} setOpen={setIsOpen} ariaLabelledby="my-title">…</Modal>
 *
 * Only compose the parts (ModalRoot, ModalTrigger, ModalPortal, ModalOverlay,
 * ModalContent, ModalClose, ModalDismissArea) directly when the trigger has to
 * live somewhere else in the tree than the modal (see TeamCard).
 */
export const Modal = ({
  children,
  trigger,
  open,
  setOpen,
  closeLabel,
  absolutePosition,
  portalRef,
  contentClassName,
  onMountedChange,
  animation,
  contentReady,
  ...accessibility
}: ModalProps) => (
  <ModalRoot
    open={open}
    setOpen={setOpen}
    absolutePosition={absolutePosition}
    onMountedChange={onMountedChange}
    animation={animation}
    contentReady={contentReady}
  >
    {trigger && <ModalTrigger>{trigger}</ModalTrigger>}
    <ModalPortal portalRef={portalRef}>
      <ModalOverlay />
      <ModalContent {...accessibility} className={contentClassName}>
        <ModalClose aria-label={closeLabel} />
        {children}
      </ModalContent>
    </ModalPortal>
  </ModalRoot>
);

export default Modal;
